import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ShoppingBasketOutlined from "@mui/icons-material/ShoppingBasketOutlined";
import React, { useCallback } from "react";
import { auth, currentSchoolKey } from "../api/firebase";
import { useBasketStrings } from "./basket/basket-strings";
import { useOrderStrings } from "./order-strings";
import { useOrderInfoBannerMinimized } from "./useOrderInfoBannerMinimized";

type OrderInfoBannerSectionProps = {
    doubleLayout: boolean;
    renderSingleLayoutText: () => React.ReactNode;
    renderDoubleLayoutOrdersText: () => React.ReactNode;
    renderDoubleLayoutCancellationsText: () => React.ReactNode;
};

export default function OrderInfoBannerSection({
    doubleLayout,
    renderSingleLayoutText,
    renderDoubleLayoutOrdersText,
    renderDoubleLayoutCancellationsText,
}: OrderInfoBannerSectionProps) {
    const orderStrings = useOrderStrings();
    const basketStrings = useBasketStrings();
    const { uid } = auth.currentUser;
    const { minimized, dismiss, restore } = useOrderInfoBannerMinimized(uid, currentSchoolKey);

    const handleToggle = useCallback(() => {
        if (minimized) {
            restore();
            return;
        }
        dismiss();
    }, [dismiss, minimized, restore]);

    return (
        <div className="order-info-banner-section">
            <div className={`order-info-accordion${minimized ? " order-info-accordion--collapsed" : " order-info-accordion--expanded"}`}>
                <button
                    type="button"
                    className="order-info-accordion-header"
                    onClick={handleToggle}
                    aria-expanded={!minimized}
                    aria-label={minimized ? orderStrings.restoreOrderInfoAria : orderStrings.dismissOrderInfoAria}
                >
                    <img className="order-info-accordion-header-icon" src="/img-assets/info.svg" alt="" />
                    <span className="order-info-accordion-title">{orderStrings.infoBoxTitle}</span>
                    {minimized
                        ? <ExpandMoreIcon className="order-info-accordion-chevron" aria-hidden />
                        : <ExpandLessIcon className="order-info-accordion-chevron" aria-hidden />
                    }
                </button>
                {!minimized && (
                    <div className={`order-info-accordion-body${doubleLayout ? "" : " order-info-accordion-body-single"}`}>
                        {doubleLayout ? (
                            <>
                                <div className="order-info-accordion-column order-info-accordion-column-orders">
                                    <div className="order-info-accordion-column-heading">
                                        <img className="order-info-accordion-column-icon" src="/img-assets/order.svg" alt="" />
                                        <span>{orderStrings.doubleLayoutOrdersHeading}</span>
                                    </div>
                                    <div className="order-info-accordion-column-body">{renderDoubleLayoutOrdersText()}</div>
                                </div>
                                <div className="order-info-accordion-column order-info-accordion-column-cancellations">
                                    <div className="order-info-accordion-column-heading">
                                        <img className="order-info-accordion-column-icon" src="/img-assets/x-circled.svg" alt="" />
                                        <span>{orderStrings.doubleLayoutCancelHeading}</span>
                                    </div>
                                    <div className="order-info-accordion-column-body">{renderDoubleLayoutCancellationsText()}</div>
                                </div>
                            </>
                        ) : (
                            <div className="order-info-accordion-column order-info-accordion-column-single">
                                <div className="order-info-accordion-column-body">{renderSingleLayoutText()}</div>
                            </div>
                        )}
                        <div className="basket-multi-day-hint">
                            <div className="basket-multi-day-hint__icon-wrap" aria-hidden>
                                <ShoppingBasketOutlined className="basket-multi-day-hint__icon" />
                            </div>
                            <p className="basket-multi-day-hint__text">{basketStrings.multiDayBasketHint}</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
